import React, { useState, useEffect } from 'react';
import { rptV2Api, rptApi } from '../services/api';
import '../styles/Modal.css';

/**
 * Modal for generating RPT V2 (Schedule III / IndAS compliant)
 * 
 * This modal is only shown for EL_SAP_B1 entities and provides
 * enhanced RPT classification using the 82 Schedule III natures.
 */
function GenerateRPTV2Modal({ entity, glVersion, onClose, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  
  // Date state (from glVersion if available)
  const [startDate, setStartDate] = useState(glVersion?.start_date || '2024-04-01');
  const [endDate, setEndDate] = useState(glVersion?.end_date || '2025-03-31');
  
  // LLM Classification option
  const [useLLMClassification, setUseLLMClassification] = useState(false);
  const [parentCompany, setParentCompany] = useState('');
  const [llmMaxConcurrentBatches, setLlmMaxConcurrentBatches] = useState(1);

  useEffect(() => {
    checkV2Eligibility();
  }, [entity]);

  const checkV2Eligibility = async () => {
    setCheckingEligibility(true);
    try {
      const response = await rptV2Api.checkEligibility(entity.name);
      setEligibility(response);
    } catch (err) {
      console.error('Error checking V2 eligibility:', err);
      setEligibility({ eligible: false, message: 'Failed to check eligibility' });
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await rptV2Api.generateRPT(entity.name, startDate, endDate, {
        useLLMClassification,
        parentCompany: parentCompany || null,
        industry: 'Healthcare / Life Sciences',
        llmMaxConcurrentBatches: useLLMClassification ? llmMaxConcurrentBatches : 1,
      });
      setResult(response);
      
      if (onComplete) {
        onComplete(response);
      }
    } catch (err) {
      console.error('RPT V2 generation error:', err);
      if (err.isApiError) {
        setError(err.data?.error || err.message);
      } else {
        setError(err.message || 'Failed to generate RPT V2');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.storage) return;

    try {
      const blob = await rptV2Api.downloadRPT(
        entity.name,
        result.storage.timestamp,
        result.storage.period
      );
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RPT_V2_${entity.name}_${result.storage.period}_${result.storage.timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download RPT V2 file');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Generate RPT V2</h2>
          <span className="modal-badge v2">Schedule III / IndAS</span>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {checkingEligibility ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Checking V2 eligibility...</p>
            </div>
          ) : !eligibility?.eligible ? (
            <div className="alert alert-warning">
              <strong>Not Eligible for RPT V2</strong>
              <p>{eligibility?.message}</p>
              <p className="small">
                RPT V2 is only available for EL_SAP_B1 entities. 
                Please use the standard RPT generation for this entity.
              </p>
            </div>
          ) : (
            <>
              {/* Entity Info */}
              <div className="info-section">
                <h4>Entity Information</h4>
                <table className="info-table">
                  <tbody>
                    <tr>
                      <td><strong>Entity:</strong></td>
                      <td>{entity.description || entity.name}</td>
                    </tr>
                    <tr>
                      <td><strong>Connection Type:</strong></td>
                      <td>
                        <span className="badge badge-success">
                          {eligibility?.connection_type || 'EL_SAP_B1'}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Classification Method:</strong></td>
                      <td>Schedule III / IndAS (82 RPT Natures)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* GL Version Info */}
              {glVersion && (
                <div className="info-section">
                  <h4>GL Data Source</h4>
                  <table className="info-table">
                    <tbody>
                      <tr>
                        <td><strong>Period:</strong></td>
                        <td>{glVersion.start_date} to {glVersion.end_date}</td>
                      </tr>
                      <tr>
                        <td><strong>Timestamp:</strong></td>
                        <td>{glVersion.timestamp}</td>
                      </tr>
                      <tr>
                        <td><strong>Total Entries:</strong></td>
                        <td>{glVersion.row_count?.toLocaleString() || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Date Selection */}
              <div className="form-section">
                <h4>Period Selection</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="startDate">Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="endDate">End Date</label>
                    <input
                      type="date"
                      id="endDate"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Classification Options */}
              <div className="form-section classification-section">
                <h4>Classification Options</h4>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={useLLMClassification}
                      onChange={(e) => setUseLLMClassification(e.target.checked)}
                      disabled={loading}
                    />
                    <span className="checkbox-text">
                      Use AI Classification (LLM)
                    </span>
                    <span className="checkbox-badge">~$3-4/entity</span>
                  </label>
                  <p className="checkbox-description">
                    Uses Claude AI for more accurate nature classification. 
                    Recommended for entities with complex transactions.
                    Takes ~5-6 minutes to process.
                  </p>
                </div>
                
                {useLLMClassification && (
                  <div className="llm-options">
                    <div className="form-group">
                      <label htmlFor="parentCompany">Parent Company (for context)</label>
                      <input
                        type="text"
                        id="parentCompany"
                        value={parentCompany}
                        onChange={(e) => setParentCompany(e.target.value)}
                        placeholder="e.g., Chemopharm Sdn Bhd"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="llmMaxConcurrentBatches">Parallel batches (speed)</label>
                      <select
                        id="llmMaxConcurrentBatches"
                        value={llmMaxConcurrentBatches}
                        onChange={(e) => setLlmMaxConcurrentBatches(Number(e.target.value))}
                        disabled={loading}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <option key={n} value={n}>
                            {n === 1 ? '1 (sequential)' : `${n} parallel`}
                          </option>
                        ))}
                      </select>
                      <p className="field-hint">3–5 recommended for faster runs; stay within API rate limits.</p>
                    </div>
                    <div className="llm-info">
                      <span className="info-icon">ℹ️</span>
                      <span>AI classification will analyze transaction context to determine accurate nature categories (Sales, Purchases, Management fees, etc.)</span>
                    </div>
                  </div>
                )}
              </div>

              {/* V2 Features */}
              <div className="info-section features-section">
                <h4>RPT V2 Features</h4>
                <ul className="feature-list">
                  <li>✓ Schedule III / IndAS compliant classification</li>
                  <li>✓ 82 specific RPT nature categories</li>
                  <li>✓ Dynamic RPT GL code fetching from SAP (U_RelatedParty='Y')</li>
                  <li>✓ AI-powered transaction classification</li>
                  <li>✓ Formula-driven Excel output with SUMIFS</li>
                  <li>✓ Exclude functionality for manual adjustments</li>
                </ul>
              </div>

              {/* Error Display */}
              {error && (
                <div className="alert alert-danger">
                  <strong>Error:</strong> {error}
                </div>
              )}

              {/* Result Display */}
              {result && result.success && (
                <div className="alert alert-success">
                  <strong>RPT V2 Generated Successfully!</strong>
                  <table className="result-table">
                    <tbody>
                      <tr>
                        <td>Total GL Entries:</td>
                        <td>{result.rpt_summary?.total_gl_entries?.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td>RPT Account Codes:</td>
                        <td>{result.rpt_summary?.rpt_account_codes_count}</td>
                      </tr>
                      <tr>
                        <td>Detected RPT Rows:</td>
                        <td>{result.rpt_summary?.detected_rpt_rows?.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td>P&L Rows:</td>
                        <td>{result.rpt_summary?.pnl_rows?.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td>Aggregated Rows:</td>
                        <td>{result.rpt_summary?.aggregated_entries}</td>
                      </tr>
                      <tr>
                        <td>Classification:</td>
                        <td>
                          <span className={`method-badge ${result.rpt_summary?.classification_method?.includes('LLM') ? 'llm' : 'deterministic'}`}>
                            {result.rpt_summary?.classification_method || 'Deterministic'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {result?.success ? (
            <>
              <button className="btn btn-primary" onClick={handleDownload}>
                Download RPT V2 Excel
              </button>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </>
          ) : (
            <>
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={loading || !eligibility?.eligible}
              >
                {loading ? (
                  <>
                    <span className="spinner-small"></span>
                    Generating...
                  </>
                ) : (
                  'Generate RPT V2'
                )}
              </button>
              <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-badge {
          margin-left: 10px;
          padding: 4px 10px;
          font-size: 12px;
          border-radius: 12px;
          font-weight: normal;
        }
        .modal-badge.v2 {
          background: linear-gradient(135deg, #2e75b6, #1a4971);
          color: white;
        }
        .info-section {
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        .info-section h4 {
          margin: 0 0 10px 0;
          color: #333;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .info-table {
          width: 100%;
        }
        .info-table td {
          padding: 5px 0;
          font-size: 14px;
        }
        .info-table td:first-child {
          width: 150px;
          color: #666;
        }
        .form-section {
          margin-bottom: 20px;
        }
        .form-section h4 {
          margin: 0 0 10px 0;
          font-size: 14px;
        }
        .form-row {
          display: flex;
          gap: 20px;
        }
        .form-group {
          flex: 1;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-size: 13px;
          color: #666;
        }
        .form-group input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        .features-section {
          background: linear-gradient(135deg, #e8f4f8, #f0f8ff);
        }
        .feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .feature-list li {
          padding: 5px 0;
          font-size: 13px;
          color: #2e75b6;
        }
        .badge {
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 12px;
        }
        .badge-success {
          background: #28a745;
          color: white;
        }
        .result-table {
          margin-top: 10px;
          width: 100%;
        }
        .result-table td {
          padding: 4px 0;
          font-size: 13px;
        }
        .result-table td:first-child {
          color: #666;
          width: 150px;
        }
        .loading-state {
          text-align: center;
          padding: 40px;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #2e75b6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        .spinner-small {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid #fff;
          border-top: 2px solid transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-right: 8px;
          vertical-align: middle;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .small {
          font-size: 12px;
          margin-top: 10px;
          color: #666;
        }
        .classification-section {
          background: #fff9e6;
          border: 1px solid #ffc107;
        }
        .checkbox-group {
          margin-bottom: 10px;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          font-size: 14px;
          gap: 8px;
        }
        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #2e75b6;
        }
        .checkbox-text {
          font-weight: 600;
          color: #333;
        }
        .checkbox-badge {
          background: linear-gradient(135deg, #ff9800, #f57c00);
          color: white;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }
        .checkbox-description {
          margin: 8px 0 0 26px;
          font-size: 12px;
          color: #666;
          line-height: 1.4;
        }
        .field-hint {
          margin: 6px 0 0 0;
          font-size: 11px;
          color: #666;
        }
        .llm-options {
          margin-top: 15px;
          padding: 15px;
          background: white;
          border-radius: 6px;
          border: 1px dashed #ccc;
        }
        .llm-info {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-top: 12px;
          padding: 10px;
          background: #e8f4f8;
          border-radius: 4px;
          font-size: 12px;
          color: #2e75b6;
        }
        .info-icon {
          font-size: 14px;
        }
        .method-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }
        .method-badge.deterministic {
          background: #e8f4e8;
          color: #28a745;
        }
        .method-badge.llm {
          background: #e8f0ff;
          color: #2e75b6;
        }
      `}</style>
    </div>
  );
}

export default GenerateRPTV2Modal;
